<script lang="ts">
export default {
  name: "BaseAlert",
};
</script>

<script setup lang="ts">
export type Props = {
  state?:
    | "primary"
    | "success"
    | "danger"
    | "warning"
    | "info"
    | "light"
    | "hint";
  close?: boolean;
  title?: string;
  description?: string;
  squareTop?: boolean;
  squareBottom?: boolean;
  smallTitle?: boolean;
};
const { getContent } = useSlotsWithSlotContent();

withDefaults(defineProps<Props>(), {
  state: "info",
  close: false,
  title: "",
  description: "",
  squareTop: false,
  squareBottom: false,
});

const hasActionsSlotContent = computed(() => getContent("actions"));
</script>

<template>
  <div
    :class="`alert alert-${state} ${close ? 'alert-dismissible' : ''}
      ${squareTop ? 'alert--square-top' : ''}
      ${squareBottom ? 'alert--square-bottom' : ''}`"
    :data-testid="`alert-${state}`"
    role="alert"
  >
    <div class="d-flex flex-column w-100">
      <h3
        v-if="title"
        class="alert__title"
        :class="{ 'alert__title--small': smallTitle }"
      >
        {{ title }}
      </h3>
      <div class="d-flex flex-column w-100 h-100 justify-content-between">
        <div class="alert__body">
          <!-- description string will only be shown if nothing is passed into the default slot -->
          <slot>{{ description }}</slot>
        </div>

        <div v-if="hasActionsSlotContent" class="mt-3 alert__actions">
          <slot name="actions" />
        </div>
      </div>
    </div>

    <div
      class="alert__second-column"
      :style="`${close ? 'margin-right: 1.5rem' : ''}`"
    >
      <slot name="second-column"></slot>
    </div>
    <button
      v-if="close"
      class="btn-close"
      type="button"
      data-bs-dismiss="alert"
      aria-label="Close"
    ></button>
  </div>
</template>

<style lang="scss" scoped>
.alert {
  display: flex;
  justify-content: space-between;
  color: $color-black;
  border-radius: $border-radius;
  padding: $spacer-75 $spacer-3;
  font-size: $font-size-sm;

  :deep(a) {
    font-weight: $font-weight-semibold;
  }

  @media print {
    background-color: transparent;
  }

  &-info {
    color: $brand-tools;
  }

  &:has(&__title) {
    padding: $spacer-3;
  }

  &__title {
    font-size: $font-size-base;
    font-weight: $font-weight-semibold;
    margin-bottom: $spacer-2;
  }

  &__title--small {
    font-size: $font-size-sm;
  }

  &__body {
    :deep(ol, ul) {
      padding-left: $spacer-3;
      margin-bottom: 0;
    }

    :deep(a:not(.btn) span) {
      // This is necessary to make sure overrides coming from Strapi do not deviate from the standard design
      font-weight: $font-weight-semibold !important;
    }

    .btn {
      width: fit-content;
      margin-bottom: $spacer-1;
    }

    :deep(p:last-of-type) {
      margin-bottom: 0;
    }
  }

  &__second-column {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    align-self: flex-end;
    height: 100%;

    img {
      width: 100px;
      height: auto;
      object-fit: cover;
      max-height: 120px;
      margin-left: 1rem;
    }
  }

  &__actions {
    display: flex;

    :deep(.btn) {
      width: fit-content;
    }
  }

  .alert-success .alert-description {
    font-weight: 600;
  }

  &-warning {
    color: $warning-text-color;
  }

  &--square-top {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  &--square-bottom {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    margin-bottom: 0;
  }
}
</style>
